import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../Root/ProtectedRoute";
import { empty, errorResponse, isObject, isString, prepareResponseData } from "../../Utilities/utils";
import { FaCaretDown, FaCaretUp, FaCheck, FaTimes } from "react-icons/fa";
import { Avatar } from "primereact/avatar";

// CSS
import "../students/Students.css";
import "./Attendance.css";

// API
import attendanceApi from "../../api/Attendance";

// Components
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import AttendanceWrapper from "../../components/appWrapper/AttendanceWrapper";
import QRCodeScanner from "../../components/scanner/QRCodeScanner";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import colors from "../../config/colors";

// Image
import student_avatar from "../../assets/avatars/profile_avatar.png";
import InputFieldChange from "../../components/form/InputFieldChange";
import { Form, Formik } from "formik";

const AttendanceScanner = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  // Ref
  const toastTR = useRef(null);
  // States
  const [studentData, setStudentData] = useState({});
  const [studentDataPanelOpen, setStudentDataPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [manualFieldsOpen, setManualFieldsOpen] = useState(false);
  const [manualDates, setManualDates] = useState({ date: '', time: '' });

  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const handleScanResult = (data) => {
    handleSubmit(data?.regno);
  };

  const handleSubmit = async (regno) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(regno)) {
        closeProfile();
        return responseDialog(
          "error",
          "Error Alert",
          "Failed to fetch user details. Please refresh the page or try again later."
        );
      }

      const school_id = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await attendanceApi.getStudentAttendanceDetails(
        school_id,
        regno
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data.success)) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to retrieve student data"
        );
      }
      const studentData =
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : {};
      setStudentData(studentData);
      return setStudentDataPanelOpen(true);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const closeProfile = () => {
    setStudentDataPanelOpen(false);
    setStudentData({});
  };

  
  const submitAttendance = async (type) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(studentData)) {
        return responseDialog(
          "error",
          "Error Alert",
          "Something went wrong! Try again later."
        );
      }

      const schoolId = !empty(studentData.schoolId) ? studentData.schoolId : "";
      const studentId = !empty(studentData._id) ? studentData._id : "";
      const date = manualDates?.date || ""; 
      const time = manualDates?.time || ""; 

      const response = await attendanceApi.recordAttendance(
        schoolId,
        studentId,
        type,
        date,
        time
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data.success)) {
        const checkedIn = isObject(response_data?.others) && response_data?.others?.checkedIn ? response_data?.others?.checkedIn : false;
        const checkedOut = isObject(response_data?.others) && response_data?.others?.checkedOut ? response_data?.others?.checkedOut : false;

        if (checkedIn) {
          setStudentData((prev) => { return { ...prev, checkedIn }})
        }
        if (checkedOut) {
          setStudentData((prev) => { return { ...prev, checkedOut }})
        }
        return responseDialog(
          "error",
          "Error Alert",
          response_data?.response || response_data?.message || ''
        );
      }
      closeProfile();
      return responseDialog(
        "success",
        "Success",
        "Attendance recorded successfully."
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };


  const handleChangeFunc = (fieldName, setFieldValue) => (e) => {
    const value =
      !empty(e) &&
      !empty(e.target) &&
      !empty(e.target.value) &&
      isString(e.target.value)
        ? e.target.value
        : "";
    
    setStudentData((prev) => { return { ...prev, checkedIn: false, checkedOut: false }})
    setFieldValue(fieldName, value);
    setManualDates((prev) => { return { ...prev, [fieldName]: value }});
  }

  return (
    <>
      <AttendanceWrapper {...props}>
        <main className="mt-80">
          {!empty(studentData) && studentDataPanelOpen ? (
            <div className="attendance-profile-box">
              <div className="attendance-profile-close">
                <FaTimes color={colors.danger} onClick={() => closeProfile()} />
              </div>
              <div className="attendance-profile-header">Student Details</div>
              <div className="attendance-profile-avatar">
                <Avatar
                  image={
                    !empty(studentData?.avatar)
                      ? studentData?.avatar
                      : student_avatar
                  }
                  shape="circle"
                  style={{ width: 120, height: 120 }}
                />
              </div>
              <div className="attendance-profile-body">
                <p>
                  <strong className="mr-10">Reg. No:</strong>
                  {!empty(studentData?.regNo) ? " " + studentData?.regNo : ""}
                </p>
                <p>
                  <strong className="mr-10">First Name:</strong>
                  {!empty(studentData?.firstName)
                    ? " " + studentData?.firstName
                    : ""}
                </p>
                <p>
                  <strong className="mr-10">Last Name:</strong>
                  {!empty(studentData?.lastName)
                    ? " " + studentData?.lastName
                    : ""}
                </p>
                <p>
                  <strong className="mr-10">Middle Name:</strong>
                  {!empty(studentData?.middleName)
                    ? " " + studentData?.middleName
                    : ""}
                </p>
                <p>
                  <strong className="mr-10">Class:</strong>
                  {!empty(studentData?.currentClass)
                    ? " " + studentData?.currentClass
                    : ""}
                </p>
              </div>
                {/* Enter manual date and time */}
                <div className="mt-20 pointer" onClick={() => setManualFieldsOpen(!manualFieldsOpen)}>
                  <span style={{ color: colors.primary }}>To enter Date and Time manually, click here { manualFieldsOpen ? <FaCaretUp /> : <FaCaretDown />}</span>
                </div>
                { manualFieldsOpen && <div className="mb-20 mt-20" style={{ width: "30%" }}>
                  <Formik
                    enableReinitialize
                    initialValues={{ date: "", time: "" }}
                  >
                    {({ values, setFieldValue }) => (
                      <Form style={{ width: "100%" }}>
                        <div className="attendance-dashboard-filter">
                          <InputFieldChange
                            placeholder="Enter Date"
                            name="date"
                            height={50}
                            type="date"
                            labelTitle="Date"
                            onChange={handleChangeFunc("date", setFieldValue)}
                          />
                        </div>
                        <div className="attendance-dashboard-filter mt-20">
                          <InputFieldChange
                            placeholder="Enter Time"
                            name="time"
                            height={50}
                            type="time"
                            labelTitle="Time"
                            onChange={handleChangeFunc("time", setFieldValue)}
                          />
                        </div>

                        <div>
                          { values?.class_group && 
                          <ButtonIcon
                            height={50}
                            marginTop={3}
                            color="#ffffff"
                            backgroundColor="#633ccd"
                            width={150}
                            borderColor="#633ccd"
                            buttonText="Generate"
                            type="submit"
                          />}
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>}
                {studentData?.checkedIn && (
                  <div
                    style={{
                      marginTop: 20,
                      color: colors.success,
                    }}
                  >
                    <FaCheck />
                    &nbsp;&nbsp;Already Checked-In
                  </div>
                )}
                {studentData?.checkedOut && (
                  <div
                    style={{
                      marginTop: 10,
                      color: colors.danger,
                      marginBottom: 20,
                    }}
                  >
                    <FaCheck />
                    &nbsp;&nbsp;Already Checked-Out
                  </div>
                )}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    columnGap: 8,
                  }}
                >
                  {!studentData?.checkedIn && 
                    <ButtonIcon
                      height={45}
                      marginTop={5}
                      color="#ffffff"
                      backgroundColor={colors.primary}
                      width={120}
                      borderColor={colors.primary}
                      buttonText="Check-In"
                      onClick={() => submitAttendance("check-in")}
                    />
                  }
                  {!studentData?.checkedOut && studentData?.checkedIn && 
                    <ButtonIcon
                      height={45}
                      marginTop={5}
                      color="#ffffff"
                      backgroundColor={colors.danger}
                      width={120}
                      borderColor={colors.danger}
                      buttonText="Check-Out"
                      onClick={() => submitAttendance("check-out")}
                    />
                  }
                </div>
              </div>
            </div>
          ) : (
            <section>
              <QRCodeScanner onScanResult={handleScanResult} />
            </section>
          )}
        </main>
        {(isLoading) && (
          <FullPageLoader visible={isLoading} />
        )}
      </AttendanceWrapper>

      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default AttendanceScanner;
